import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Coins, CheckCircle2, Circle, Clock, Sparkles, RefreshCw, Plus, Trash2, X, Edit2 } from "lucide-react";
import CopyButton from "@/components/CopyButton";
import ContextualImportButton from "@/components/ContextualImportButton";
import ContextButton from "@/components/ContextButton";
import DataStateTrendBadge from "@/components/DataStateTrendBadge";
import SourceCalcBtn from "@/components/SourceCalcBtn";
import MarketSizeWidget from "@/components/MarketSizeWidget";
import ModuleHelpButton from "@/components/ModuleHelpButton";
import ThreeQPageHeader from "@/components/ThreeQPageHeader";
import { useLocalContext, describeScope } from "@/lib/moduleContext";
import { useActiveDoctrine, doctrinePayload } from "@/lib/doctrine";
import { useDemoMode } from "@/lib/demoMode";
import { notify, fmtError } from "@/lib/notify";
import CreditRating from "@/components/CreditRating";

function FinTabs({ tab, setTab }) {
  return (
    <div className="flex gap-1 border-b border-white/5">
      {[["actifs", "Actifs & LTV"], ["rating", "Notation crédit"]].map(([v, label]) => (
        <button
          key={v}
          onClick={() => setTab(v)}
          className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px transition ${tab === v ? "border-[#378ADD] text-white" : "border-transparent text-zinc-500 hover:text-zinc-200"}`}
          data-testid={`fin-tab-${v}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

const FIN_IMPORT_FIELDS = [
  { key: "nom", label: "Nom de l'actif", required: true },
  { key: "valeur", label: "Valeur", type: "int", default: 0 },
  { key: "ltv", label: "LTV (0..1)", type: "float", default: 0.5 },
  { key: "garantie", label: "Garantie", default: "" },
  { key: "phase", label: "Phase", default: "Production" },
  { key: "classe", label: "Classe d'actif", default: "Autres actifs" },
  { key: "localisation", label: "Localisation", default: "" },
  { key: "devise", label: "Devise", default: "CHF" },
  { key: "echeance", label: "Échéance", default: "" },
  { key: "referent", label: "Référent", default: "" },
];

const PHASE_STATUS_COLOR = { "fait": "#1D9E75", "en cours": "#378ADD", "à venir": "#A1A1AA" };
const PHASE_STATUS_ICON = { "fait": CheckCircle2, "en cours": Clock, "à venir": Circle };
const PHASES = ["Diagnostic", "Structuration", "Sécurisation", "Financement", "Expansion", "Consolidation"];

function formatEur(n) {
  if (!n && n !== 0) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} M€`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)} k€`;
  return `${n} €`;
}
function ltvColor(ltv) {
  if (ltv == null) return "#A1A1AA";
  if (ltv >= 0.75) return "#E24B4A";
  if (ltv >= 0.65) return "#EF9F27";
  return "#1D9E75";
}

export default function Financement() {
  const isDemo = useDemoMode();
  const endpoint = isDemo ? "/financement/summary" : "/workspace/financement/summary";
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [commentary, setCommentary] = useState(null);
  const [loadingC, setLoadingC] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ nom: "", valeur: 1_000_000, ltv: 0.6, garantie: "", phase: "Diagnostic" });
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [scope] = useLocalContext("financement");
  const activeDoctrine = useActiveDoctrine();
  const [mainTab, setMainTab] = useState("actifs");

  const isEditing = editingId !== null;
  const resetForm = () => {
    setForm({ nom: "", valeur: 1_000_000, ltv: 0.6, garantie: "", phase: "Diagnostic" });
    setEditingId(null);
  };

  const openEdit = (a) => {
    setForm({
      nom: a.nom || "",
      valeur: a.valeur ?? 1_000_000,
      ltv: a.ltv ?? 0.6,
      garantie: a.garantie || "",
      phase: a.phase || "Diagnostic",
    });
    setEditingId(a.id);
    setShowAdd(true);
  };

  const load = () => api.get(endpoint).then((r) => setData(r.data)).catch((e) => setError(e?.response?.data?.detail || "Erreur"));

  useEffect(() => { setData(null); setError(null); load(); /* eslint-disable-next-line */ }, [isDemo]);

  const generate = async () => {
    setLoadingC(true);
    try { const r = await api.post("/financement/commentary", { context_hint: describeScope(scope), doctrine: doctrinePayload(activeDoctrine) }); setCommentary(r.data.commentary); }
    catch { setCommentary("Désolé, le commentaire n'a pas pu être généré."); }
    finally { setLoadingC(false); }
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        valeur: parseInt(form.valeur, 10),
        ltv: parseFloat(form.ltv),
      };
      if (isEditing) {
        await api.patch(`/workspace/financement/actifs/${editingId}`, payload);
      } else {
        await api.post("/workspace/financement/actifs", payload);
      }
      setShowAdd(false);
      resetForm();
      await load();
    } catch (err) { notify.error(fmtError(err, "Erreur")); }
    finally { setBusy(false); }
  };

  const removeActif = async (id) => {
    if (!window.confirm("Supprimer cet actif ?")) return;
    try { await api.delete(`/workspace/financement/actifs/${id}`); await load(); }
    catch (err) { notify.error(fmtError(err, "Erreur")); }
  };

  if (error) return <div className="text-[#E24B4A] text-sm" data-testid="fin-error">{error}</div>;
  if (!data) return <div className="text-zinc-400">Chargement…</div>;

  if (!isDemo && mainTab === "rating") {
    return (
      <div className="space-y-6" data-testid="financement-root">
        <div>
          <div className="flex items-center gap-2 text-[#378ADD] noam-overline"><Coins className="w-3 h-3" /> Tactique · Capital</div>
          <h1 className="noam-display text-3xl font-bold tracking-tight mt-1">Module Financement</h1>
        </div>
        <FinTabs tab={mainTab} setTab={setMainTab} />
        <CreditRating />
      </div>
    );
  }

  if (!isDemo && data.is_empty) {
    return (
      <div className="space-y-6" data-testid="financement-root">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 text-[#378ADD] noam-overline"><Coins className="w-3 h-3" /> Tactique · Capital</div>
            <h1 className="noam-display text-3xl font-bold tracking-tight mt-1">Module Financement</h1>
            <p className="text-zinc-400 text-sm mt-2 max-w-3xl">
              Cartographiez vos actifs et leur LTV pour piloter votre finançabilité. Noam ne finance pas — il rend votre organisation finançable en la rendant lisible.
            </p>
          </div>
          <button onClick={() => setShowAdd(true)} className="btn-primary" data-testid="fin-add-btn">
            <Plus className="w-4 h-4" /> Ajouter un actif
          </button>
        </div>
        <FinTabs tab={mainTab} setTab={setMainTab} />
        <div className="noam-card p-12 text-center">
          <Coins className="w-12 h-12 text-[#378ADD] mx-auto opacity-60" />
          <div className="noam-display text-xl mt-4">Votre module Financement est vide</div>
          <p className="text-zinc-400 text-sm mt-2 max-w-md mx-auto">
            Aucun actif n'est encore enregistré. Ajoutez vos actifs (immobilier, lignes de crédit, stock…) pour calculer votre score de finançabilité et votre LTV global.
          </p>
          <button onClick={() => setShowAdd(true)} className="btn-primary mt-6 mx-auto inline-flex" data-testid="fin-empty-add">
            <Plus className="w-4 h-4" /> Ajouter votre premier actif
          </button>
        </div>
        <AddActifModal open={showAdd} onClose={() => { setShowAdd(false); resetForm(); }} form={form} setForm={setForm} onSubmit={submitAdd} busy={busy} isEditing={isEditing} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="financement-root">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 text-[#378ADD] noam-overline"><Coins className="w-3 h-3" /> Tactique · Capital</div>
          <h1 className="noam-display text-3xl font-bold tracking-tight mt-1">Module Financement</h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-3xl">
            Module du capital — finançabilité, LTV par actif, séquençage des 6 phases.
          </p>
        </div>
        {!isDemo && (
          <div className="flex items-center gap-2">
            <ModuleHelpButton moduleKey="financement" />
            <ContextButton moduleKey="financement" />
            <ContextualImportButton moduleKey="financement" targetFields={FIN_IMPORT_FIELDS} onImported={() => window.location.reload()} />
            <button onClick={() => setShowAdd(true)} className="btn-primary" data-testid="fin-add-btn">
              <Plus className="w-4 h-4" /> Ajouter un actif
            </button>
          </div>
        )}
      </div>

      {/* V23.16 — Onglets Actifs & LTV / Notation crédit (Bloomfield) */}
      {!isDemo && <FinTabs tab={mainTab} setTab={setMainTab} />}

      {/* V23.15.35 — Doctrine 3Q · deal capital prioritaire */}
      <ThreeQPageHeader endpoint="/workspace/financement/top-priority" />

      <MarketSizeWidget moduleKey="financement" title="Module Financement" />

      <div className="grid md:grid-cols-3 gap-4">
        <div className="noam-card p-5 relative" data-testid="fin-financabilite">
          <SourceCalcBtn kpiKey="fin_score" />
          <div className="text-xs text-zinc-400">Score finançabilité</div>
          <div className="flex items-end justify-between mt-3 gap-2">
            <div className="noam-display text-3xl font-bold text-[#378ADD]">
              {data.score_financabilite}<span className="text-base text-zinc-500">/100</span>
            </div>
            <DataStateTrendBadge
              data={data.score_financabilite}
              etat={data.score_financabilite >= 70 ? "bon" : data.score_financabilite >= 50 ? "attention" : "critique"}
              tendance="stable" polarity="positive" compact
            />
          </div>
          <div className="h-1.5 mt-3 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full bg-[#378ADD]" style={{ width: `${data.score_financabilite}%` }} />
          </div>
        </div>
        <div className="noam-card p-5 relative" data-testid="fin-ltv">
          <SourceCalcBtn kpiKey="fin_ltv" />
          <div className="text-xs text-zinc-400">LTV moyen portefeuille</div>
          <div className="flex items-end justify-between mt-3 gap-2">
            <div className="noam-display text-3xl font-bold" style={{ color: ltvColor(data.ltv_global) }}>
              {(data.ltv_global * 100).toFixed(0)}<span className="text-base text-zinc-500">%</span>
            </div>
            <DataStateTrendBadge
              data={`${(data.ltv_global * 100).toFixed(0)}%`}
              etat={data.ltv_global >= 0.75 ? "critique" : data.ltv_global >= 0.65 ? "attention" : "bon"}
              tendance="stable" polarity="negative" compact
            />
          </div>
          <div className="text-[11px] text-zinc-500 mt-1">Seuil d'alerte : 75%</div>
        </div>
        <div className="noam-card p-5" data-testid="fin-cout-inaction">
          <div className="text-xs text-zinc-400">{isDemo ? "Coût d'inaction financier" : "Exposition LTV ≥ 75%"}</div>
          <div className="noam-display text-3xl font-bold mt-3 text-[#E24B4A]">
            {formatEur(data.cout_inaction_financier)}
          </div>
          <div className="text-[11px] text-zinc-500 mt-1">
            {isDemo ? "Estimation sur risques non traités" : "Valeur cumulée des actifs sensibles"}
          </div>
        </div>
      </div>

      <div className="noam-card p-6" data-testid="fin-phases">
        <div className="noam-overline mb-1">Séquençage</div>
        <div className="noam-display text-base font-semibold mb-5">6 phases de structuration financière</div>
        <div className="grid md:grid-cols-6 gap-3">
          {data.phases.map((ph, i) => {
            const Icon = PHASE_STATUS_ICON[ph.status] || Circle;
            const c = PHASE_STATUS_COLOR[ph.status];
            return (
              <div key={ph.n} className="relative" data-testid={`fin-phase-${ph.n}`}>
                {i < data.phases.length - 1 && (<div className="hidden md:block absolute top-4 left-[calc(50%+18px)] right-0 h-px bg-white/10" />)}
                <div className="flex md:flex-col items-center md:items-start gap-3">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: `${c}1A`, border: `1px solid ${c}55` }}>
                    <Icon className="w-4 h-4" style={{ color: c }} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Phase {ph.n}</div>
                    <div className="text-sm font-medium text-white">{ph.label}</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5 capitalize">{ph.status}</div>
                  </div>
                </div>
                <div className="text-[11px] text-zinc-400 mt-2 leading-relaxed hidden md:block">{ph.desc}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="noam-card overflow-hidden" data-testid="fin-actifs">
        <div className="px-5 py-4 border-b border-white/5">
          <div className="noam-overline">Actifs</div>
          <div className="noam-display text-base font-semibold">LTV par actif et phase</div>
        </div>
        <table className="w-full text-sm">
          <thead className="text-xs text-zinc-500 border-b border-white/5 bg-white/[0.02]">
            <tr>
              <th className="text-left py-2.5 px-5 font-normal">Actif</th>
              <th className="text-right font-normal">Valeur</th>
              <th className="text-center font-normal">LTV</th>
              <th className="text-left font-normal hidden md:table-cell">Garantie</th>
              <th className="text-left font-normal">Phase</th>
              {!isDemo && <th className="text-center font-normal w-12"></th>}
            </tr>
          </thead>
          <tbody>
            {data.actifs.map((a) => (
              <tr key={a.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                <td className="py-3 px-5 text-white">{a.nom}</td>
                <td className="text-right text-zinc-300">{formatEur(a.valeur)}</td>
                <td className="text-center">
                  <span className="text-xs px-2 py-0.5 rounded font-semibold" style={{ background: `${ltvColor(a.ltv)}26`, color: ltvColor(a.ltv) }}>
                    {(a.ltv * 100).toFixed(0)}%
                  </span>
                </td>
                <td className="text-zinc-400 hidden md:table-cell">{a.garantie || "—"}</td>
                <td className="text-zinc-300">{a.phase}</td>
                {!isDemo && (
                  <td className="text-center">
                    <div className="inline-flex items-center gap-0.5">
                      <button onClick={() => openEdit(a)} className="text-zinc-500 hover:text-[#378ADD] p-1" data-testid={`fin-edit-${a.id}`} title="Modifier">
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => removeActif(a.id)} className="text-zinc-500 hover:text-[#E24B4A] p-1" data-testid={`fin-del-${a.id}`} title="Supprimer">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!isDemo && <CollateralCard />}

      {isDemo && (
        <div className="noam-card p-6" data-testid="fin-commentary">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#378ADD]" />
              <div>
                <div className="noam-overline">Lecture IA · Finançabilité</div>
                <div className="noam-display text-base font-semibold">Analyse Noam</div>
              </div>
            </div>
            <button onClick={generate} disabled={loadingC} className="btn-primary text-xs disabled:opacity-50" data-testid="fin-generate">
              <RefreshCw className={`w-3.5 h-3.5 ${loadingC ? "animate-spin" : ""}`} />
              {commentary ? "Régénérer" : "Générer le commentaire"}
            </button>
          </div>
          {!commentary && !loadingC && <p className="text-zinc-400 text-sm">Cliquez sur « Générer » pour obtenir une lecture narrative de votre finançabilité.</p>}
          {loadingC && (<div className="flex items-center gap-2 text-zinc-400 text-sm"><span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" /><span className="ml-2">Noam analyse…</span></div>)}
          {commentary && (<><p className="text-zinc-200 leading-relaxed border-l-2 border-[#378ADD] pl-4 italic" data-testid="fin-commentary-text">{commentary}</p><div className="mt-4"><CopyButton text={commentary} label="Copier le commentaire" /></div></>)}
        </div>
      )}

      <AddActifModal open={showAdd} onClose={() => { setShowAdd(false); resetForm(); }} form={form} setForm={setForm} onSubmit={submitAdd} busy={busy} isEditing={isEditing} />
    </div>
  );
}

function AddActifModal({ open, onClose, form, setForm, onSubmit, busy, isEditing = false }) {
  if (!open) return null;
  const ASSET_CLASSES = [
    { v: "immobilier", label: "Immobilier", ltv_default: 0.65 },
    { v: "valeurs_mobilieres", label: "Valeurs mobilières", ltv_default: 0.6 },
    { v: "equipements", label: "Équipements industriels", ltv_default: 0.5 },
    { v: "creances", label: "Créances commerciales", ltv_default: 0.55 },
    { v: "art", label: "Art / collections", ltv_default: 0.3 },
    { v: "autre", label: "Autre", ltv_default: 0.4 },
  ];
  const updateClass = (cls) => {
    const meta = ASSET_CLASSES.find((a) => a.v === cls);
    setForm({ ...form, classe: cls, ltv: form.ltv || meta?.ltv_default || 0.5 });
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="noam-card w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="fin-add-modal">
        <div className="flex items-center justify-between mb-1">
          <div>
            <div className="noam-overline">Financement</div>
            <div className="noam-display text-lg font-semibold">{isEditing ? "Modifier l'actif" : "Nouvel actif finançable"}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-zinc-500 mb-5">Plus l'actif est qualifié, mieux il pourra être structuré. La LTV se pré-remplit selon la classe d'actif.</p>
        <form onSubmit={onSubmit} className="space-y-4">
          {/* Identité */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="text-xs text-zinc-400 mb-1 block">Nom de l'actif <span className="text-[#E24B4A]">*</span></label>
              <input required value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })}
                placeholder="Ex : Portefeuille immobilier Genève — 3 immeubles"
                className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none"
                data-testid="fin-form-nom" />
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Classe d'actif</label>
              <select value={form.classe || ""} onChange={(e) => updateClass(e.target.value)}
                className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none"
                data-testid="fin-form-classe">
                <option value="">— Choisir —</option>
                {ASSET_CLASSES.map((a) => <option key={a.v} value={a.v}>{a.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Localisation / Pays</label>
              <input value={form.localisation || ""} onChange={(e) => setForm({ ...form, localisation: e.target.value })}
                placeholder="Suisse · Genève"
                className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none"
                data-testid="fin-form-localisation" />
            </div>
          </div>

          {/* Valeur & LTV */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Valeur (€) <span className="text-[#E24B4A]">*</span></label>
              <input type="number" required min="0" value={form.valeur} onChange={(e) => setForm({ ...form, valeur: e.target.value })}
                className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                data-testid="fin-form-valeur" />
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Devise</label>
              <select value={form.devise || "EUR"} onChange={(e) => setForm({ ...form, devise: e.target.value })}
                className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                data-testid="fin-form-devise">
                {["EUR", "CHF", "USD", "GBP"].map((d) => <option key={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">LTV cible (0–1) <span className="text-[#E24B4A]">*</span></label>
              <input type="number" required step="0.01" min="0" max="1" value={form.ltv}
                onChange={(e) => setForm({ ...form, ltv: e.target.value })}
                className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                data-testid="fin-form-ltv" />
            </div>
          </div>

          {/* Sécurisation */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Garantie / Sûreté</label>
              <input value={form.garantie} onChange={(e) => setForm({ ...form, garantie: e.target.value })}
                placeholder="Hypothèque 1er rang"
                className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                data-testid="fin-form-garantie" />
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Échéance contrat</label>
              <input type="date" value={form.echeance || ""} onChange={(e) => setForm({ ...form, echeance: e.target.value })}
                className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                data-testid="fin-form-echeance" />
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Phase de structuration</label>
              <select value={form.phase} onChange={(e) => setForm({ ...form, phase: e.target.value })}
                className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                data-testid="fin-form-phase">
                {PHASES.map((p) => (<option key={p} value={p}>{p}</option>))}
              </select>
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Référent / Banque</label>
              <input value={form.referent || ""} onChange={(e) => setForm({ ...form, referent: e.target.value })}
                placeholder="UBS · M. Dupont"
                className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                data-testid="fin-form-referent" />
            </div>
          </div>

          {/* Note */}
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Note</label>
            <textarea rows={2} value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="Conditions particulières, points d'attention…"
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white resize-none focus:border-[#378ADD] focus:outline-none"
              data-testid="fin-form-note" />
          </div>

          {/* Live preview */}
          {form.valeur > 0 && form.ltv > 0 && (
            <div className="bg-[#378ADD]/10 border border-[#378ADD]/30 rounded-md px-3 py-2.5 flex items-center justify-between text-xs">
              <div className="text-zinc-400">Finançable estimé</div>
              <div className="text-white font-semibold">
                {Math.round(parseFloat(form.valeur || 0) * parseFloat(form.ltv || 0)).toLocaleString("fr-FR")} {form.devise || "EUR"}
                <span className="text-zinc-500 ml-2 font-normal">({Math.round((form.ltv || 0) * 100)}% LTV)</span>
              </div>
            </div>
          )}

          <button type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-50" data-testid="fin-form-submit">
            {busy ? "Enregistrement…" : (isEditing ? "Enregistrer les modifications" : "Ajouter l'actif")}
          </button>
        </form>
      </div>
    </div>
  );
}

const COLLATERAL_CATEGORIES = [
  "Immobilier résidentiel", "Immobilier commercial", "Actions cotées",
  "Équipements industriels", "Art / collection", "Autres actifs",
];

function CollateralCard() {
  const [data, setData] = useState(null);
  const [assets, setAssets] = useState([{ category: COLLATERAL_CATEGORIES[0], valeur_eur: 0 }]);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = () =>
    api.get("/workspace/positionnement").then((r) => {
      setData(r.data);
      if (r.data?.collateral?.assets?.length) {
        setAssets(r.data.collateral.assets.map((a) => ({ category: a.category, valeur_eur: a.valeur_eur })));
      }
    }).catch(() => {});

  useEffect(() => { load(); }, []);

  const update = (i, k, v) => setAssets((arr) => arr.map((a, idx) => idx === i ? { ...a, [k]: v } : a));
  const add = () => setAssets((arr) => [...arr, { category: COLLATERAL_CATEGORIES[0], valeur_eur: 0 }]);
  const remove = (i) => setAssets((arr) => arr.filter((_, idx) => idx !== i));
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/workspace/positionnement/collateral", {
        assets: assets.map((a) => ({ category: a.category, valeur_eur: parseInt(a.valeur_eur, 10) || 0 })),
      });
      await load();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally { setBusy(false); }
  };

  return (
    <div className="noam-card p-6" data-testid="fin-collateral">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <div>
          <div className="noam-overline">Capital · Asset-back</div>
          <div className="noam-display text-base font-semibold">Collatéral disponible</div>
        </div>
        {saved && <span className="text-xs text-[#1D9E75] flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Enregistré</span>}
      </div>
      <p className="text-xs text-zinc-500 mb-5">Renseignez vos actifs mobilisables. Le LTV moyen et le score de finançabilité asset-backed sont calculés automatiquement et alimentent le score global.</p>
      <div className="space-y-2">
        {assets.map((a, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`fin-collateral-row-${i}`}>
            <select value={a.category} onChange={(e) => update(i, "category", e.target.value)}
              className="col-span-7 sm:col-span-6 bg-black/30 border border-white/10 rounded-md px-2 py-2 text-sm text-white"
              data-testid={`fin-collateral-cat-${i}`}>
              {COLLATERAL_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <input type="number" min="0" value={a.valeur_eur}
              onChange={(e) => update(i, "valeur_eur", e.target.value)}
              placeholder="Valeur €"
              className="col-span-4 sm:col-span-5 bg-black/30 border border-white/10 rounded-md px-2 py-2 text-sm text-white"
              data-testid={`fin-collateral-val-${i}`} />
            <button onClick={() => remove(i)} disabled={assets.length === 1}
              className="col-span-1 text-zinc-500 hover:text-[#E24B4A] p-1 justify-self-center disabled:opacity-30">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        <button onClick={add} className="text-xs text-[#378ADD] hover:underline flex items-center gap-1 pt-1" data-testid="fin-collateral-add">
          <Plus className="w-3 h-3" /> Ajouter un actif collatéral
        </button>
      </div>
      {data?.collateral && (
        <div className="grid grid-cols-3 gap-3 pt-4 mt-4 border-t border-white/5">
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Total actifs</div>
            <div className="noam-display text-xl font-semibold mt-0.5">{formatEur(data.collateral.total_valeur_eur)}</div>
          </div>
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Finançable</div>
            <div className="noam-display text-xl font-semibold text-[#1D9E75] mt-0.5">{formatEur(data.collateral.total_finançable_eur)}</div>
          </div>
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Score asset-back</div>
            <div className="noam-display text-xl font-semibold text-[#378ADD] mt-0.5">{data.collateral.score_finançabilite}/100</div>
          </div>
        </div>
      )}
      <div className="flex justify-end pt-4">
        <button onClick={save} disabled={busy} className="btn-primary text-xs disabled:opacity-50" data-testid="fin-collateral-save">
          {busy ? "Enregistrement…" : "Enregistrer le collatéral"}
        </button>
      </div>
    </div>
  );
}
